



























































import { bus } from "@/bus";
import { Getter } from "vuex-class";
import { fixedHtmlData, fileUploadStages, ContainerDimensions } from "@/services/posts/postTypes/fixedhtml";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { BehaviorEnum } from "@/services/posts/types";
import BehaviorDropdown from "@/components/posts/BehaviorDropdown.vue";
import { MediaType } from "@/services/files/types";
import i18n from "@/i18n";
import VideoSubComponent from "./mediaSubComponents/VideoSubComponent.vue";
import ImageSubComponent from "./mediaSubComponents/ImageSubComponent.vue";
import VueCropperAdjust from "@/components/common/cropperjs/VueCropperAdjust.vue";
import ResizeObserver from "resize-observer-polyfill";

@Component({
    components: {
        BehaviorDropdown,
        ImageSubComponent,
        VideoSubComponent,
        VueCropperAdjust
    }
})
export default class MediaComponentForm extends Vue {
    // Props
    @Prop() vsid!: string;
    @Prop() styles!: string;
    @Prop() fileuploadinprogress!: fileUploadStages;
    @Getter("authentication/getUserHasFolderAccess") getUserHasFolderAccess: any;
    @Getter("getAllowStockImages") allowStockImages!: boolean;

    // - Data Properties -
    mediaType = MediaType;

    // Media Drag and drop
    isDraggedOver = false;

    // Behavior display State
    showBehavior = false;

    selectedMediaType = MediaType.Image;

    // Image manipulation properties
    ratio = 1;
    isBehaviorDisabled = false;
    hideBehaviorToolbar = false;
    showAdjust = true;

    // Media Container dimensions
    containerDimensions: ContainerDimensions = {
        width: 0,
        height: 0
    };

    // Video display properties
    video = {
        width: 0,
        height: 0
    };

    adjustContainerSize = {
        width: 0,
        height: 0
    };

    // Resize observer to observe changes in the media container's size
    resizeObserver: ResizeObserver | null = null;

    // Get fixed html data from parent component.
    properties = {
        ...((this.$parent as any).localProperties.data).filter((x: fixedHtmlData) => x.id === this.vsid)[0]
    } as fixedHtmlData

    // Computed
    get src() {
        return this.properties.src || this.generatePlaceholderImage();
    }

    get dropMediaText(): string | undefined {
        if (this.properties.allowedMedia.includes(MediaType.Image) && this.properties.allowedMedia.includes(MediaType.Video)) {
            return i18n.t(`posts.fixedHtml.dropImageVideo`).toString();
        } else if (this.properties.allowedMedia.includes(MediaType.Video)) {
            return i18n.t(`posts.fixedHtml.dropVideo`).toString();
        } else if (this.properties.allowedMedia.includes(MediaType.Image)) {
            return i18n.t(`posts.fixedHtml.dropImage`).toString();
        }
    }

    get isContentTemplateForm() {
        return (this.$parent as any).localProperties.contentTemplateId === undefined;
    }

    get filteredBehaviorTypes(): Array<{ id: string, name: string }> {
        if (!this.properties.allowedResizeBehaviors || this.properties.allowedResizeBehaviors.trim() === "") {
            return this.behaviorTypes;
        }

        const allowedBehaviors: string[] = this.properties.allowedResizeBehaviors.toLowerCase().split(" ").map((e: string) => e.trim());

        // If the allowed behaviors is "none", only resize with adjust but hide the toolbar
        if (allowedBehaviors.includes("none")) {
            this.showAdjust = false;
            return this.behaviorTypes.filter((behaviorType: any) => behaviorType.id === BehaviorEnum.adjust);
        }

        // replace "fit" with "letterbox" to match the behavior type
        if (allowedBehaviors.includes("fit")) {
            allowedBehaviors[allowedBehaviors.indexOf("fit")] = "letterbox";
        }

        const includedBehaviors = this.behaviorTypes.filter((behaviorType: any) => allowedBehaviors.includes(behaviorType.id.toLowerCase()));
        var filteredBehaviors = includedBehaviors.length > 0 ? includedBehaviors : this.behaviorTypes;

        // If only one behavior is allowed and it's not adjust, disable the toolbar
        if (filteredBehaviors.length === 1 && filteredBehaviors[0].id !== BehaviorEnum.adjust) {
            this.hideBehaviorToolbar = true;
        }

        // If the allowed behavior is not selected, set it to the first allowed behavior
        if (this.properties.behavior && !filteredBehaviors.map((e: any) => e.id).includes(this.properties.behavior)) {
            this.properties.behavior = filteredBehaviors[0].id;
        }
        return filteredBehaviors;
    }

    get behaviorTypes(): any {
        return Object.keys(BehaviorEnum)
            .filter(key => {
                const condition = !(this.selectedMediaType === MediaType.Video && key === "adjust");
                return condition;
            })
            .map(key => ({ name: i18n.t(`posts.${key}`), id: key }));
    }

    // We use this to avoid responsive template to fill the whole screen.
    get displayContainerStyles() {
        if (this.isBehaviorDisabled) return "";
        return `width: ${this.containerDimensions.width}px; height: ${this.containerDimensions.height}px;`;
    }

    // Lifecycle Hooks
    mounted() {
        // Using next Tick to wait for the imageContainer to load before calculating width and height in pixels.
        Vue.nextTick(() => {
            this.calcAndSetWidthAndHeightInPixels();
        });

        if (this.properties.selectedMediaType) {
            this.selectedMediaType = this.properties.selectedMediaType;
        }

        bus.$on("changeMedia", (id: string, src: string, mediaType: MediaType) => {
            if (id === this.properties.id) {
                this.selectedMediaType = mediaType;
                // if changed media is a video, set default behavior to crop.
                if (this.selectedMediaType === MediaType.Video && this.properties.behavior === BehaviorEnum.adjust) {
                    this.properties.behavior = BehaviorEnum.crop;
                }
                this.properties.src = src;
            }
        });

        bus.$on("getComponentData", () => {
            const props = ((this.$parent as any).localProperties.data).filter((x: fixedHtmlData) => x.id === this.vsid)[0] as fixedHtmlData;
            if (props) {
                const mediaPosition = this.properties.position;
                props.behavior = this.properties.behavior;
                props.src = this.src;
                props.selectedMediaType = this.selectedMediaType;
                props.position = {
                    id: this.src,
                    x: mediaPosition.x,
                    y: mediaPosition.y,
                    editorX: mediaPosition.editorX,
                    editorY: mediaPosition.editorY,
                    cropSizeWidth: mediaPosition.cropSizeWidth,
                    cropSizeHeight: mediaPosition.cropSizeHeight,
                    factor: mediaPosition.factor,
                    channelWidth: mediaPosition.channelWidth, // TODO: Should rename to container Width
                    channelHeight: mediaPosition.channelHeight, // TODO: Should rename to container Height
                    imgWidth: mediaPosition.imgWidth,
                    imgHeight: mediaPosition.imgHeight,
                    videoWidth: this.video.width,
                    videoHeight: this.video.height
                };
                this.properties = { ...props };
            }
        });
    }

    // Methods
    generatePlaceholderImage() {
        return "https://placehold.jp/" + this.containerDimensions.width + "x" + this.containerDimensions.height + ".png";
    }

    destroyed() {
        bus.$off("changeMedia");
        bus.$off("getComponentData");

        // Disconnect the ResizeObserver when the component is destroyed
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    // Methods

    calcAndSetWidthAndHeightInPixels() {
        const mediaContainerElement = this.$refs.mediaContainer as HTMLElement;
        // If the height is not set, set it to 100% and add it to the styles string which will be used to calculate the width and height in pixels.
        if (!(this.styles || "").includes("height")) {
            mediaContainerElement.style.height = "100%";
        }

        this.calculateMediaContainerDimensions(mediaContainerElement);
    }

    calculateMediaContainerDimensions(mediaContainerElement: HTMLElement) {
        // Create a ResizeObserver to monitor changes in the media container's size
        this.resizeObserver = new ResizeObserver((mediaContainer) => {
            var { width, height } = mediaContainer[0].contentRect;
            if (height > 0 && width > 0) {
                this.containerDimensions = {
                    width: width,
                    height: height
                };
            }
        });

        // Start observing the media container element
        this.resizeObserver.observe(mediaContainerElement);
    }

    changeMedia() {
        bus.$emit("openFileSelect", this.properties.id, this.properties.allowedMedia);
    }

    directUpload() {
        bus.$emit("openDirectUpload", this.properties.id, this.properties.allowedMedia);
    }

    onSetDuration(duration: number) {
        bus.$emit("setDuration", duration, this.vsid);
    }

    stockImageDownload() {
        bus.$emit("stockImageDownload", this.properties.id);
    }

    dragEnter() {
        this.isDraggedOver = true;
    }

    dragLeave() {
        this.isDraggedOver = false;
    }

    dragOver(e: any) {
        e.preventDefault();
    }

    drop(e: any) {
        this.isDraggedOver = false;
        e.preventDefault();
        const file = e.dataTransfer.files[0]; // Since an media component only holds 1 media, there's no need to support multi-upload.
        if (file) bus.$emit("injectFile", file, this.properties.id, this.properties.allowedMedia);
    }

    onAdjustPropertiesUpdated(localProperties: fixedHtmlData, ratio: number) {
        // Create a new properties object with updated position which will trigger a watcher in the child components
        this.properties = {
            ...this.properties,
            position: { ...localProperties.position }
        };
        this.ratio = ratio;
        const imgWidth = localProperties.position.imgWidth;
        const imgHeight = localProperties.position.imgHeight;
        const factor = localProperties.position.factor;

        this.adjustContainerSize.width = (imgWidth * factor) * this.ratio;
        this.adjustContainerSize.height = (imgHeight * factor) * this.ratio;
    }

    onVideoLoaded(videoSize: any) {
        // to save video size
        this.video.width = videoSize.width;
        this.video.height = videoSize.height;
    }
}
